import { useEffect } from 'react';
import { css } from '@emotion/react';
import { PersonsV3 } from '@frontend/api-person';
import { convertToCents } from '@frontend/currency';
import { useTranslation } from '@frontend/i18n';
import { useInvalidateInvoices } from '@frontend/payments-invoice-controller';
import {
  CreateInvoiceForm,
  DropZoneOverlay,
  shouldShowModal,
  ThresholdModals,
  useCreateInvoiceForm,
  useCreatePaymentRequest,
} from '@frontend/person-invoice-create';
import { pendo } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { getTodaysDate, useAlert, useModalControl } from '@frontend/design-system';
import { useCollectPaymentMultiContext } from '../../../collect-payment-multi.context';
import { useScopedTrackingIds } from '../../../hooks/use-scoped-tracking-ids';
import { PAYMENT_MULTI_STEP_LABELS, PAYMENT_MULTI_STEPS } from '../../../utils/steps';
import { StyledStep } from '../../atoms/styled-step';
import { CreateStepBodyProps } from './create.types';

const styles = {
  body: css`
    margin-bottom: ${theme.spacing(2)};
  `,
};

export const CreateBody = ({
  primary,
  secondary,
  isCreating,
  onGoBack,
  setIsCreating,
  onCreateRequest,
  onClickSecondary,
}: CreateStepBodyProps) => {
  const { t } = useTranslation('payments');
  const { person, origin } = useCollectPaymentMultiContext();
  const alerts = useAlert();

  const {
    createFormProps,
    dropZoneProps,
    resetFields,
    attachment,
    selectedPerson: V2SelectedPerson,
    skipAttachmentAuth,
  } = useCreateInvoiceForm();

  const {
    form: { values, isComplete },
  } = createFormProps;
  const { createPaymentRequest } = useCreatePaymentRequest({
    locationId: values.locationId,
    origin,
    onError: (error) => {
      /**
       * Override error handling here, so we don't have 2 alerts
       * If it's a "weave user" error, show it.
       */
      if (error instanceof Error) {
        if (error.message.includes('weave user')) {
          alerts.error(error.message);
        } else {
          alerts.error(t('Could not create invoice.'));
        }
      }
    },
  });
  const { modalProps: thresholdModalProps, triggerProps: thresholdTriggerProps } = useModalControl();

  const { primaryTrackingId, backTrackingId } = useScopedTrackingIds({
    primaryTrackingId: 'create--primary-btn',
    backTrackingId: 'create--back-btn',
  });

  const goBack = () => {
    onGoBack();
  };
  const invalidateInvoices = useInvalidateInvoices();

  const handleCreateRequest = async () => {
    setIsCreating(true);
    return await createPaymentRequest({
      personId: values.personId!,
      amount: convertToCents(Number(values.amount)),
      attachment,
      mobileNumber: V2SelectedPerson?.MobilePhone,
      memo: values.memo,
      providerName: values.provider,
      skipAttachmentAuth,
    })
      .then((invoice) => {
        pendo.track('payments-create-invoice', {
          date: getTodaysDate('MM/DD/YYYY'),
          amount: Number(values.amount),
        });
        invalidateInvoices(values.locationId);
        onCreateRequest(invoice);

        return invoice;
      })
      .catch((err) => {
        if (err instanceof Error) {
          /**
           * Do not throw error for 'weave user' errors.
           */
          if (!err.message.includes('weave user')) {
            throw err;
          }
        }
        return null;
      })
      .finally(() => {
        setIsCreating(false);
      });
  };

  useEffect(() => {
    if (person && !V2SelectedPerson) {
      createFormProps.setSelectedPerson(PersonsV3.PersonHelpers.convertPersonV3ToPerson(person));
    }
  }, [person, V2SelectedPerson]);

  const onClickCreate = async () => {
    return shouldShowModal(createFormProps.form.values.amount)
      ? thresholdTriggerProps.onClick()
      : handleCreateRequest();
  };

  return (
    <StyledStep
      id={PAYMENT_MULTI_STEPS.create}
      label={PAYMENT_MULTI_STEP_LABELS[PAYMENT_MULTI_STEPS.create]}
      isValid={isComplete}
      footer={{
        primary: primary || {
          children: isCreating ? t('Creating...') : t('Collect Payment'),
          trackingId: primaryTrackingId,
          iconName: 'pay-small',
          disabled: !createFormProps.form.isComplete || isCreating,
        },
        secondary: Object.assign({}, secondary, {
          disabled: !createFormProps.form.isComplete || isCreating,
        }),
        back: {
          children: t('Back'),
          trackingId: backTrackingId,
        },
        onClickSecondary: () => {
          onClickSecondary({
            resetFields: () => resetFields(),
            handleCreateRequest,
          });
        },
        onClickPrimary: () => onClickCreate(),
        onClickBack: goBack,
      }}
    >
      <div className='invoice-body' css={styles.body}>
        <DropZoneOverlay {...dropZoneProps}>
          <CreateInvoiceForm {...createFormProps} />
        </DropZoneOverlay>
      </div>
      <ThresholdModals
        {...thresholdModalProps}
        amount={createFormProps.form.values.amount}
        onConfirm={handleCreateRequest}
      />
    </StyledStep>
  );
};
